import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import type {
  DecisionDecideInput,
  DecisionFeatureError,
  DecisionSupersedeInput,
  DecisionView,
} from '../types'

export interface DecisionActionFormProps {
  decision: DecisionView
  pending: boolean
  error: DecisionFeatureError | null
  onDecide(input: DecisionDecideInput): Promise<boolean>
  onSupersede(input: DecisionSupersedeInput): Promise<boolean>
  onDismissError(): void
}

/** Inline, draft-owned decision acts. A server refusal deliberately leaves the form mounted. */
export function DecisionActionForm({
  decision,
  pending,
  error,
  onDecide,
  onSupersede,
  onDismissError,
}: DecisionActionFormProps) {
  const { t } = useTranslation('projectWorkspace')
  const [optionKey, setOptionKey] = useState(decision.options[0]?.key ?? '')
  const [rationale, setRationale] = useState('')
  const [replacementRef, setReplacementRef] = useState('')
  const [reason, setReason] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const alertRef = useRef<HTMLDivElement>(null)
  const isDecide = decision.status === 'OPEN'
  const isSupersede = decision.status === 'DECIDED'

  useEffect(() => {
    if (error !== null) alertRef.current?.focus()
  }, [error])

  if (!isDecide && !isSupersede) return null

  const onSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    event.preventDefault()
    setSubmitted(true)
    if (isDecide && (optionKey === '' || rationale.trim() === '')) return
    if (isSupersede && (replacementRef.trim() === '' || reason.trim() === ''))
      return

    const successful = isDecide
      ? await onDecide({
          chosenOptionKey: optionKey,
          rationale: rationale.trim(),
        })
      : await onSupersede({
          supersededByRef: replacementRef.trim(),
          reason: reason.trim(),
        })
    if (successful) onDismissError()
  }

  const errorId = `decision-action-error-${decision.ref}`
  const optionInvalid = submitted && optionKey === ''
  const rationaleInvalid = submitted && rationale.trim() === ''
  const referenceInvalid = submitted && replacementRef.trim() === ''
  const reasonInvalid = submitted && reason.trim() === ''

  return (
    <form
      className="mt-4 border-t border-border pt-3"
      onSubmit={(event) => void onSubmit(event)}
      noValidate
    >
      <fieldset className="space-y-3" disabled={pending} aria-busy={pending}>
        <legend className="sr-only">
          {isDecide
            ? t('blueprint.decisions.decide.title')
            : t('blueprint.decisions.supersede.title')}
        </legend>
        {isDecide ? (
          <>
            <div>
              <label
                className="text-sm font-medium text-foreground"
                htmlFor={`decision-option-${decision.ref}`}
              >
                {t('blueprint.decisions.decide.option')}
              </label>
              <select
                id={`decision-option-${decision.ref}`}
                value={optionKey}
                onChange={(event) => {
                  setOptionKey(event.target.value)
                  onDismissError()
                }}
                aria-required="true"
                aria-invalid={optionInvalid}
                aria-describedby={optionInvalid ? errorId : undefined}
                className="mt-1 h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">
                  {t('blueprint.decisions.decide.optionPlaceholder')}
                </option>
                {decision.options.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                className="text-sm font-medium text-foreground"
                htmlFor={`decision-rationale-${decision.ref}`}
              >
                {t('blueprint.decisions.decide.rationale')}
              </label>
              <Input
                id={`decision-rationale-${decision.ref}`}
                value={rationale}
                onChange={(event) => {
                  setRationale(event.target.value)
                  onDismissError()
                }}
                aria-required="true"
                aria-invalid={rationaleInvalid}
                aria-describedby={rationaleInvalid ? errorId : undefined}
                placeholder={t(
                  'blueprint.decisions.decide.rationalePlaceholder'
                )}
              />
            </div>
          </>
        ) : (
          <>
            <div>
              <label
                className="text-sm font-medium text-foreground"
                htmlFor={`decision-replacement-${decision.ref}`}
              >
                {t('blueprint.decisions.supersede.replacementRef')}
              </label>
              <Input
                id={`decision-replacement-${decision.ref}`}
                value={replacementRef}
                onChange={(event) => {
                  setReplacementRef(event.target.value)
                  onDismissError()
                }}
                aria-required="true"
                aria-invalid={referenceInvalid}
                aria-describedby={referenceInvalid ? errorId : undefined}
                placeholder={t(
                  'blueprint.decisions.supersede.replacementPlaceholder'
                )}
              />
            </div>
            <div>
              <label
                className="text-sm font-medium text-foreground"
                htmlFor={`decision-reason-${decision.ref}`}
              >
                {t('blueprint.decisions.supersede.reason')}
              </label>
              <Input
                id={`decision-reason-${decision.ref}`}
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value)
                  onDismissError()
                }}
                aria-required="true"
                aria-invalid={reasonInvalid}
                aria-describedby={reasonInvalid ? errorId : undefined}
                placeholder={t(
                  'blueprint.decisions.supersede.reasonPlaceholder'
                )}
              />
            </div>
          </>
        )}
        {error !== null && (
          <div
            ref={alertRef}
            id={errorId}
            tabIndex={-1}
            role="alert"
            className="rounded-md border border-blocked bg-blocked-bg px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <p className="font-medium">
              {t('blueprint.decisions.conflict.title')}
            </p>
            <p className="mt-1">{t('blueprint.decisions.conflict.body')}</p>
            <p className="mt-1 font-mono text-xs">
              {error.status}
              {error.code ? ` · ${error.code}` : ''}
              {error.message ? ` · ${error.message}` : ''}
            </p>
          </div>
        )}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button type="submit" className="w-full sm:w-auto">
            {pending
              ? t('blueprint.decisions.pending')
              : isDecide
                ? t('blueprint.decisions.decide.submit')
                : t('blueprint.decisions.supersede.submit')}
          </Button>
        </div>
      </fieldset>
    </form>
  )
}
